import { useCallback, useMemo, useState } from 'react'
import type { LcLedgerChannel, LcLedgerEntryType, LcSettlementParams } from '@/api/lcSettlement'

/** Longest inclusive period the backend accepts is 366 days, so dateTo may be at most dateFrom + 365 days. */
export const MAX_RANGE_OFFSET_DAYS = 365

export interface LcSettlementFilterDraft {
  dateFrom: string
  dateTo: string
  organizationId: string
  channel: '' | LcLedgerChannel
  entryType: '' | LcLedgerEntryType
  includeTest: boolean
}

const kstDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** Today's calendar date in Asia/Seoul as YYYY-MM-DD, regardless of the browser timezone. */
export function kstToday(now: Date = new Date()): string {
  return kstDateFormatter.format(now)
}

export function firstDayOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`
}

export function addDays(date: string, days: number): string {
  const base = new Date(`${date}T00:00:00Z`)
  if (Number.isNaN(base.getTime())) return date
  base.setUTCDate(base.getUTCDate() + days)
  return base.toISOString().slice(0, 10)
}

/** Latest dateTo allowed for a given dateFrom. */
export function maxDateTo(dateFrom: string): string | undefined {
  return dateFrom ? addDays(dateFrom, MAX_RANGE_OFFSET_DAYS) : undefined
}

export function defaultFilters(today: string = kstToday()): LcSettlementFilterDraft {
  return {
    dateFrom: firstDayOfMonth(today),
    dateTo: today,
    organizationId: '',
    channel: '',
    entryType: '',
    includeTest: true,
  }
}

export function validateFilters(draft: LcSettlementFilterDraft): string | null {
  if (!draft.dateFrom || !draft.dateTo) return '시작일과 종료일을 모두 입력하세요.'
  if (draft.dateFrom > draft.dateTo) return '시작일이 종료일보다 늦을 수 없습니다.'
  const limit = maxDateTo(draft.dateFrom)
  if (limit && draft.dateTo > limit) return '조회 기간은 최대 366일까지 설정할 수 있습니다.'
  return null
}

export function toParams(filters: LcSettlementFilterDraft): LcSettlementParams {
  return {
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    organizationId: filters.organizationId ? Number(filters.organizationId) : undefined,
    channel: filters.channel || undefined,
    entryType: filters.entryType || undefined,
    includeTest: filters.includeTest,
  }
}

/** Draft/apply filter state shared by the summary and entries tabs. */
export function useLcSettlementFilters() {
  const [draft, setDraft] = useState<LcSettlementFilterDraft>(() => defaultFilters())
  const [filters, setFilters] = useState<LcSettlementFilterDraft>(draft)
  const [error, setError] = useState<string | null>(null)

  const apply = useCallback(() => {
    const message = validateFilters(draft)
    setError(message)
    if (message) return false
    setFilters(draft)
    return true
  }, [draft])

  const reset = useCallback(() => {
    const next = defaultFilters()
    setDraft(next)
    setFilters(next)
    setError(null)
  }, [])

  const params = useMemo(() => toParams(filters), [filters])

  return { draft, setDraft, filters, params, error, apply, reset }
}
